import React from "react";
import { connect } from "react-redux";
import SideBarPanel from "./SideBarPanel";
import MapLayerSelector from "./MapLayerSelector";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { reorderSidebarItem } from "../../../Redux/actions/sidebarItems.js";
import { toggleSidebar } from "../../../Redux/actions/sidebarUI.js";
import Inventum from "../../../../engine/Inventum";
import withRouter from "../../../withRouter.js";
import { selectPermissionCapabilities } from "../../../utils/permissions.js";
import { getPanelSortableId } from "./sortableIds.js";


function CreateCollection(props) {
  let handleClick = (e) => {
    e.preventDefault();
    Inventum.models.createGroup();
  };
  return (
    <div className="CreateNewCollection" onClick={handleClick}>
      Create New Group
    </div>
  );
}

function SideBarTitle(props) {
  let style = {
    backgroundImage: "url(/static/img/logo/inventum_logo.png)",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
  };

  return (
    <div className="SideBarTitle">
      <img
        title="Back to Inventum Home"
        onClick={() => {
          props.history.push("/");
        }}
        src="/static/img/logo/inventum_logo.png"
        alt="Inventum Logo"
      />
    </div>
  );
}

function PanelContainer(props) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const sortablePanels = props.panelContent.filter((item) => !item || item.sortable !== false);
    const oldIndex = sortablePanels.findIndex((item, index) => getPanelSortableId(item, index) === active.id);
    const newIndex = sortablePanels.findIndex((item, index) => getPanelSortableId(item, index) === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const sortEndData = {
      oldIndex: oldIndex,
      newIndex: newIndex,
    };

    props.onSortEnd(sortEndData);
  };

  let content = props.panelContent;
  if (content === undefined) {
    return null;
  }

  let disableDrag = true;
  let showCreateCollection = false;

  if (props.pageMode === "2D") {
    disableDrag = false;
  }

  if (props.pageMode === "3D" && props.editMode && props.siteAdmin) {
    showCreateCollection = true;
    disableDrag = false;
  }

  const pinnedContent = content.filter((panel) => panel && panel.sortable === false && panel.pinnedPosition !== "bottom");
  const bottomPinnedContent = content.filter((panel) => panel && panel.sortable === false && panel.pinnedPosition === "bottom");
  const sortableContent = content.filter((panel) => !panel || panel.sortable !== false);
  const panelIds = sortableContent.map(getPanelSortableId).filter((id) => id !== null);
  const panelContainerClassName = [
    "PanelContainer",
    props.pageMode === "2D" ? "noGap" : "",
    showCreateCollection ? "withCreateButton" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={panelContainerClassName}>
      <div className="TabHeader">{props.pageMode === "2D" ? "Active Map Layers" : "3D Models"}</div>

      <div className="PanelList">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
          {pinnedContent.map((panel, index) => {
            const panelId = getPanelSortableId(panel, index);
            return <SideBarPanel key={panelId} id={panelId} index={index} content={panel} disabled />;
          })}
          <SortableContext items={panelIds} strategy={verticalListSortingStrategy}>
            {sortableContent.map((panel, index) => {
              if (panel === null || panel === undefined) {
                return null;
              }
              const panelId = getPanelSortableId(panel, index);
              return <SideBarPanel key={panelId} id={panelId} index={index} content={panel} disabled={disableDrag} />;
            })}
          </SortableContext>
          {bottomPinnedContent.map((panel, index) => {
            const panelId = getPanelSortableId(panel, index);
            return <SideBarPanel key={panelId} id={panelId} index={index} content={panel} disabled />;
          })}
        </DndContext>
      </div>

      {showCreateCollection ? <CreateCollection /> : null}
    </div>
  );
}

function ContentContainer(props) {
  return (
    <div className="ContentContainer">
      <PanelContainer
        siteAdmin={props.siteAdmin}
        editMode={props.editMode}
        pageMode={props.pageMode}
        onSortEnd={props.handleSort}
        panelContent={props.panelContent}
      />
    </div>
  );
}

class SideBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.handleSort = this.handleSort.bind(this);
  }

  handleSort(data) {
    if (this.props.pageMode === "3D" && this.props.editMode) {
      Inventum.models.sortGroups(data);
    }
    this.props.reorderSidebarItem(data);
  }

  render() {
    let pageMode = this.props.pageMode;
    return (
      <div className={"SideBar " + (this.props.sideBarVisible ? "visible" : "")}>
        <SideBarTitle history={this.props.history} toggleSidebar={this.props.toggleSidebar} />
        <ContentContainer
          siteAdmin={this.props.siteAdmin}
          editMode={this.props.editMode}
          handleSort={this.handleSort}
          toggleSidebar={this.props.toggleSidebar}
          pageMode={pageMode}
          panelContent={this.props.sidebarItems}
        />
        {this.props.pageMode === "2D" ? <MapLayerSelector /> : null}
      </div>
    );
  }
}

let mapStateToProps = (state) => {
  const caps = selectPermissionCapabilities(state);

  return {
    pageMode: state.sidebarUI.mode,
    sidebarItems: state.sidebarItems,
    sideBarVisible: state.sidebarUI.visible,
    editMode: state.ui.editMode,
    // Legacy prop name retained for compatibility; capability-based source.
    siteAdmin: caps.canAccessEditorUI,
  };
};

let mapDispatchToProps = (dispatch) => ({
  reorderSidebarItem: (data) => {
    dispatch(reorderSidebarItem(data));
  },
  toggleSidebar: () => {
    dispatch(toggleSidebar());
  },
});

const SideBarWrapper = withRouter(connect(mapStateToProps, mapDispatchToProps)(SideBar));

export default SideBarWrapper;
